import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Loader2, Send } from "lucide-react";
import { money } from "./poUtils";
import { SupplierPicker } from "@/components/production/SupplierPicker";

const UNITS = ["each", "pair", "box", "pack", "set", "sheet", "linear ft", "sq ft", "roll", "case"];
const NEW = "__new__";
const blankLine = () => ({ key: Math.random().toString(36).slice(2), catalog_item_id: "", name: "", sku: "", size: "", unit: "each", quantity: 1, unit_price: 0, notes: "", isNew: false });

// Create / edit a material procurement list. Pass `po` to edit, `projectId`/`customerId` to pre-tag.
export const ProcurementListDialog = ({ open, onOpenChange, po, projectId, customerId, onSaved }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ supplier_id: "", project_id: projectId || "", title: "", expected_date: "", notes: "", ship_to: "" });
  const [lines, setLines] = useState([blankLine()]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/suppliers`).then(r => setSuppliers(r.data || [])).catch(() => {});
    const url = customerId ? `${API}/customers/${customerId}` : `${API}/projects`;
    axios.get(url).then(r => setProjects(customerId ? (r.data.projects || []) : (r.data || []))).catch(() => setProjects([]));
    if (po) {
      setForm({ supplier_id: po.supplier_id, project_id: po.project_id || "", title: po.title || "", expected_date: po.expected_date || "", notes: po.notes || "", ship_to: po.ship_to || "" });
      setLines(po.items.map(i => ({ ...i, key: i.line_id, catalog_item_id: i.catalog_item_id || "", isNew: false })));
    } else {
      setForm({ supplier_id: "", project_id: projectId || "", title: "", expected_date: "", notes: "", ship_to: "" });
      setLines([blankLine()]);
    }
  }, [open, po, projectId, customerId]);

  useEffect(() => {
    if (!form.supplier_id) { setCatalog([]); return; }
    axios.get(`${API}/procurement/catalog?supplier_id=${form.supplier_id}`).then(r => setCatalog(r.data || [])).catch(() => setCatalog([]));
  }, [form.supplier_id]);

  const total = useMemo(() => lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0), [lines]);
  const setLine = (key, patch) => setLines(ls => ls.map(l => l.key === key ? { ...l, ...patch } : l));

  const pickCatalog = (key, value) => {
    if (value === NEW) return setLine(key, { catalog_item_id: "", isNew: true, name: "", sku: "", size: "", unit: "each", unit_price: 0 });
    const c = catalog.find(i => i.item_id === value);
    if (c) setLine(key, { catalog_item_id: c.item_id, isNew: false, name: c.name, sku: c.sku || "", size: c.size || "", unit: c.unit || "each", unit_price: c.unit_price || 0 });
    else setLine(key, { catalog_item_id: "", isNew: false, name: "" });
  };

  const save = async (thenSend) => {
    if (!form.supplier_id) return toast.error("Choose a supplier");
    const items = lines.filter(l => l.name.trim());
    if (!items.length) return toast.error("Add at least one item");
    setSaving(true);
    try {
      const payload = { ...form, project_id: form.project_id || null, customer_id: customerId || null, expected_date: form.expected_date || null,
        items: items.map(({ key, isNew, ...l }) => ({ ...l, catalog_item_id: l.catalog_item_id || null, quantity: Number(l.quantity) || 0, unit_price: Number(l.unit_price) || 0 })) };
      const r = po ? await axios.put(`${API}/procurement/lists/${po.po_id}`, payload) : await axios.post(`${API}/procurement/lists`, payload);
      let saved = r.data;
      if (thenSend) {
        const s = await axios.post(`${API}/procurement/lists/${saved.po_id}/send`);
        if (s.status === 202) toast.info(`${saved.po_number} saved — sent to owner for approval`);
        else { toast.success(`${saved.po_number} emailed to supplier`); saved = s.data; }
      } else toast.success(po ? "List updated" : `${saved.po_number} saved as draft`);
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save list"); }
    finally { setSaving(false); }
  };

  const locked = po && !["draft", "pending_approval"].includes(po.status);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl" aria-describedby={undefined} data-testid="procurement-dialog">
        <DialogHeader><DialogTitle>{po ? `Edit ${po.po_number}` : "New Material Procurement List"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Supplier *</Label>
            <div className="mt-1">
              <SupplierPicker suppliers={suppliers} value={form.supplier_id} disabled={!!po} testId="po-supplier-select" defaultCategory="hardware"
                onChange={(v) => { setForm({ ...form, supplier_id: v }); setLines([blankLine()]); }}
                onCreated={(s) => setSuppliers(prev => [...prev, s].sort((a, b) => a.company_name.localeCompare(b.company_name)))} />
            </div>
          </div>
          <div>
            <Label>Tag to Project (optional)</Label>
            <select className="w-full border rounded-md px-3 py-2 text-sm mt-1 bg-white" value={form.project_id} disabled={!!projectId}
              onChange={e => setForm({ ...form, project_id: e.target.value })} data-testid="po-project-select">
              <option value="">No project — stock / shop</option>
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}{p.customer_name ? ` — ${p.customer_name}` : ""}</option>)}
            </select>
          </div>
          <div><Label>Title</Label><Input className="mt-1" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Drawer slides & hinges" data-testid="po-title-input" /></div>
          <div><Label>Requested delivery (ETA)</Label><Input type="date" className="mt-1" value={form.expected_date} onChange={e => setForm({ ...form, expected_date: e.target.value })} data-testid="po-eta-input" /></div>
          <div className="sm:col-span-2"><Label>Ship to</Label><Input className="mt-1" value={form.ship_to} onChange={e => setForm({ ...form, ship_to: e.target.value })} placeholder="Defaults to project address" /></div>
        </div>

        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <Label>Items</Label>
            {!form.supplier_id && <span className="text-xs text-slate-400">Pick a supplier to load saved products</span>}
          </div>
          <div className="space-y-2">
            {lines.map((l, idx) => (
              <div key={l.key} className="border rounded-lg p-2 bg-slate-50/60" data-testid={`po-line-${idx}`}>
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-12 sm:col-span-5">
                    {!l.isNew && !(po && !l.catalog_item_id && l.name) ? (
                      <select className="w-full border rounded-md px-2 py-2 text-sm bg-white" value={l.catalog_item_id || ""} disabled={!form.supplier_id || locked}
                        onChange={e => pickCatalog(l.key, e.target.value)} data-testid={`po-line-item-select-${idx}`}>
                        <option value="">Select product…</option>
                        {catalog.map(c => <option key={c.item_id} value={c.item_id}>{c.name}{c.size ? ` — ${c.size}` : ""}{c.sku ? ` (${c.sku})` : ""}</option>)}
                        <option value={NEW}>＋ Add new product…</option>
                      </select>
                    ) : (
                      <div className="grid grid-cols-3 gap-1">
                        <Input className="col-span-3 sm:col-span-1 h-9" placeholder="Description *" value={l.name} onChange={e => setLine(l.key, { name: e.target.value })} data-testid={`po-line-name-${idx}`} />
                        <Input className="h-9" placeholder="SKU" value={l.sku || ""} onChange={e => setLine(l.key, { sku: e.target.value })} />
                        <Input className="h-9" placeholder="Size" value={l.size || ""} onChange={e => setLine(l.key, { size: e.target.value })} />
                      </div>
                    )}
                  </div>
                  <div className="col-span-4 sm:col-span-2"><Input type="number" min="0" step="any" className="h-9" placeholder="Qty" value={l.quantity} disabled={locked} onChange={e => setLine(l.key, { quantity: e.target.value })} data-testid={`po-line-qty-${idx}`} /></div>
                  <div className="col-span-4 sm:col-span-2">
                    <select className="w-full border rounded-md px-2 h-9 text-sm bg-white" value={l.unit} disabled={locked} onChange={e => setLine(l.key, { unit: e.target.value })}>
                      {UNITS.map(u => <option key={u}>{u}</option>)}
                    </select>
                  </div>
                  <div className="col-span-4 sm:col-span-2"><Input type="number" min="0" step="0.01" className="h-9" placeholder="Unit $" value={l.unit_price} disabled={locked} onChange={e => setLine(l.key, { unit_price: e.target.value })} data-testid={`po-line-price-${idx}`} /></div>
                  <div className="col-span-12 sm:col-span-1 flex items-center justify-between sm:justify-end gap-1">
                    <span className="text-xs font-medium text-slate-600 sm:hidden">{money((Number(l.quantity) || 0) * (Number(l.unit_price) || 0))}</span>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" disabled={locked} onClick={() => setLines(ls => ls.length > 1 ? ls.filter(x => x.key !== l.key) : [blankLine()])}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                {(l.isNew || l.name) && <Input className="mt-1 h-8 text-xs" placeholder="Line note (finish, colour, hand…)" value={l.notes || ""} disabled={locked} onChange={e => setLine(l.key, { notes: e.target.value })} />}
                {l.isNew && <p className="text-[11px] text-emerald-700 mt-1">New product — it will be saved to this supplier's catalog for next time.</p>}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-2">
            <Button variant="outline" size="sm" disabled={!form.supplier_id || locked} onClick={() => setLines(ls => [...ls, blankLine()])} data-testid="po-add-line-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add line</Button>
            <p className="text-sm font-semibold" data-testid="po-total">Total {money(total)}</p>
          </div>
        </div>

        <div className="mt-2"><Label>Notes to supplier</Label><Textarea className="mt-1" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Delivery instructions, finish, hand, etc." /></div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="secondary" disabled={saving} onClick={() => save(false)} data-testid="po-save-draft-btn">{saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{po ? "Save changes" : "Save draft"}</Button>
          {!locked && <Button disabled={saving} onClick={() => save(true)} data-testid="po-save-send-btn"><Send className="w-4 h-4 mr-1" /> Save & send to supplier</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
